/**
 * Ordnet einer Mahnung Rechnungen zu — und loest bestehende Zuordnungen wieder.
 *
 * Vor Juli 2026 gab es dafuer KEINE Oberflaeche: `invoicesApi.linkInvoices`
 * existierte, wurde aber in keiner Seite aufgerufen, und einen Unlink-Weg gab es
 * gar nicht. Eine Falschzuordnung war damit nicht korrigierbar — bei den vielen
 * zu grosszuegigen Auto-Links ein echtes Problem.
 *
 * Vorschlagsliste = die im Beleg genannten Referenznummern (ocr_raw_json),
 * darunter freie Suche.
 */
import { useEffect, useMemo, useState } from 'react';
import { Check, Link2, Loader2, Search, Unlink, X } from 'lucide-react';
import { Modal, Button } from '@/components/common';
import { invoices as invoicesApi } from '@/utils/api';
import { toast } from 'sonner';

const formatCurrency = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '-'
    : new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(v);

const formatDate = (d: string | null | undefined) =>
  d ? new Date(d).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '-';

interface Props {
  reminder: any | null;
  onClose: () => void;
  onChanged: () => void;
}

export default function ReminderAssignDialog({ reminder, onClose, onChanged }: Props) {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [missingNumbers, setMissingNumbers] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [linked, setLinked] = useState<any[]>([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Im Beleg genannte Rechnungsnummern — gleiche Split-Logik wie im Backend
  const referenceNumbers = useMemo(() => {
    if (!reminder?.ocr_raw_json) return [];
    try {
      const raw = JSON.parse(reminder.ocr_raw_json);
      if (!raw.reference_invoice_number) return [];
      return String(raw.reference_invoice_number)
        .split(/[,;/]+|\s+und\s+|\s+and\s+/)
        .map((s: string) => s.trim())
        .filter(Boolean);
    } catch {
      return [];
    }
  }, [reminder]);

  useEffect(() => {
    if (!reminder) return;
    setSelected(new Set());
    setSearch('');
    setSearchResults([]);
    setLoading(true);

    (async () => {
      try {
        const linkedRes = await invoicesApi.getLinked(reminder.id);
        setLinked(linkedRes.data || []);

        if (referenceNumbers.length > 0) {
          const res = await invoicesApi.findByNumbers(referenceNumbers);
          const found: any[] = [];
          const missing: string[] = [];
          for (const num of referenceNumbers) {
            const hit = res.data?.[num];
            if (hit) found.push(hit); else missing.push(num);
          }
          setSuggestions(found);
          setMissingNumbers(missing);
          // Treffer vorauswaehlen, aber nur was noch nicht verknuepft ist
          const alreadyLinked = new Set((linkedRes.data || []).map((l: any) => l.id));
          setSelected(new Set(found.filter((f) => !alreadyLinked.has(f.id)).map((f) => f.id)));
        } else {
          setSuggestions([]);
          setMissingNumbers([]);
        }
      } catch (e: any) {
        toast.error('Vorschläge konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reminder?.id]);

  // Freie Suche (debounced)
  useEffect(() => {
    if (!search || search.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await invoicesApi.getAll({ type: 'incoming', search });
        setSearchResults((res.data || []).slice(0, 25));
      } catch { setSearchResults([]); }
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  if (!reminder) return null;

  const linkedIds = new Set(linked.map((l: any) => l.id));
  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleLink = async () => {
    const ids = [...selected].filter((id) => !linkedIds.has(id));
    if (ids.length === 0) return;
    setSaving(true);
    try {
      await invoicesApi.linkInvoices(reminder.id, ids);
      toast.success(`${ids.length} Rechnung(en) zugeordnet`);
      const fresh = await invoicesApi.getLinked(reminder.id);
      setLinked(fresh.data || []);
      setSelected(new Set());
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Zuordnen fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlink = async (invoiceId: number) => {
    setSaving(true);
    try {
      await invoicesApi.unlinkInvoice(reminder.id, invoiceId);
      toast.success('Zuordnung aufgehoben');
      const fresh = await invoicesApi.getLinked(reminder.id);
      setLinked(fresh.data || []);
      onChanged();
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Aufheben fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const renderCandidate = (inv: any) => {
    const already = linkedIds.has(inv.id);
    return (
      <label
        key={inv.id}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg border ${
          already ? 'bg-gray-50 border-gray-200 opacity-60' : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          disabled={already}
          checked={already || selected.has(inv.id)}
          onChange={() => toggle(inv.id)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-mono">{inv.invoice_number || 'ohne Nummer'}</span>
          <span className="block text-xs text-gray-500 truncate">
            {inv.supplier_name || '-'} · {formatDate(inv.invoice_date)}
          </span>
        </span>
        <span className="text-sm font-medium">{formatCurrency(inv.gross_amount)}</span>
        {already && <span className="text-[11px] text-gray-400">zugeordnet</span>}
      </label>
    );
  };

  return (
    <Modal isOpen={!!reminder} onClose={onClose} title="Mahnung zuordnen" size="lg">
      <div className="p-4 space-y-5">
        <div className="rounded-lg bg-orange-50 border border-orange-200 px-4 py-3">
          <div className="text-sm text-orange-900">
            <span className="font-semibold">
              Mahnung{reminder.reminder_level ? ` Stufe ${reminder.reminder_level}` : ''}
            </span>{' '}
            — {reminder.supplier_name || 'Unbekannt'} · {formatCurrency(reminder.gross_amount)}
          </div>
          {referenceNumbers.length > 0 && (
            <div className="text-xs text-orange-800 mt-1">
              Im Beleg genannt: {referenceNumbers.length} Rechnungsnummer(n)
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Vorschläge werden geladen…
          </div>
        ) : (
          <>
            {linked.length > 0 && (
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                  Bereits zugeordnet ({linked.length})
                </h4>
                <div className="space-y-1.5 max-h-52 overflow-y-auto">
                  {linked.map((inv: any) => (
                    <div key={inv.id} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200">
                      <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-mono">{inv.invoice_number || 'ohne Nummer'}</span>
                        <span className="block text-xs text-gray-500 truncate">{inv.supplier_name || '-'}</span>
                      </span>
                      <span className="text-sm">{formatCurrency(inv.gross_amount)}</span>
                      <button
                        onClick={() => handleUnlink(inv.id)}
                        disabled={saving}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                        title="Zuordnung aufheben"
                      >
                        <Unlink className="w-3.5 h-3.5" /> Lösen
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {suggestions.length > 0 && (
              <section>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                  Im Beleg genannt ({suggestions.length})
                </h4>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">{suggestions.map(renderCandidate)}</div>
              </section>
            )}

            {missingNumbers.length > 0 && (
              <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                <span className="font-medium">Nicht in der Buchhaltung gefunden:</span>{' '}
                {missingNumbers.join(', ')}
                <span className="block mt-0.5 text-gray-400">
                  Diese Rechnungen wurden vermutlich noch nicht aus der Dokumentenablage übernommen.
                </span>
              </div>
            )}

            <section>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
                Freie Suche
              </h4>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechnungsnummer oder Lieferant…"
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1.5 mt-2 max-h-60 overflow-y-auto">
                  {searchResults.map(renderCandidate)}
                </div>
              )}
            </section>
          </>
        )}

        <div className="flex items-center justify-end gap-2 pt-3 border-t">
          <button onClick={onClose} className="inline-flex items-center gap-1 px-3 py-2 text-sm text-gray-600 hover:text-gray-800">
            <X className="w-4 h-4" /> Schließen
          </button>
          <Button onClick={handleLink} disabled={saving || selected.size === 0}>
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
            {selected.size} zuordnen
          </Button>
        </div>
      </div>
    </Modal>
  );
}
